import Radio from "@/components/Radio";
import { CompensationProfileFormInput } from "../types";
import { Controller, UseFormReturn } from "react-hook-form";
import TextField from "@/components/TextField";
import { NumberFormatBase } from "react-number-format";
import ListItem from "@/components/ListItem";
import { currencyFormatterNoDecimals } from "@/lib/currencyHelpers";
import { useEffect, useState, useRef } from "react";

export function GolfShopDetailsStep({ form }: { form: UseFormReturn<CompensationProfileFormInput> }) {
  // Helper to determine which option should be selected based on form values
  function getInitialOption(values: CompensationProfileFormInput): "owner" | "commission" | "none" {
    // If owns_golf_shop is explicitly true, it's definitely owner
    if (values.ownsGolfShop === true) {
      return "owner";
    }

    // If owns_golf_shop is explicitly false, check commission fields
    if (values.ownsGolfShop === false) {
      if (values.golfShopCommissionPercentage != null || values.golfShopCommissionAnnualIncome != null) {
        return "commission";
      } else {
        return "none";
      }
    }

    // If owns_golf_shop is null/undefined, use field values to determine
    const hasOwnerFields =
      values.golfShopOwnershipPercent != null || values.golfShopGrossSales != null || values.golfShopNetIncome != null;
    const hasCommissionFields =
      values.golfShopCommissionPercentage != null || values.golfShopCommissionAnnualIncome != null;

    if (hasOwnerFields && !hasCommissionFields) {
      return "owner";
    } else if (hasCommissionFields && !hasOwnerFields) {
      return "commission";
    } else {
      return "none";
    }
  }

  const [golfShopOption, setGolfShopOption] = useState<"owner" | "commission" | "none">(() =>
    getInitialOption(form.getValues())
  );

  const previousOptionRef = useRef<"owner" | "commission" | "none">(golfShopOption);

  useEffect(() => {
    const newOption = getInitialOption(form.getValues());
    setGolfShopOption(newOption);
    previousOptionRef.current = newOption;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (form.formState.isDirty === false && form.formState.isSubmitted === false) {
      const newOption = getInitialOption(form.getValues());
      setGolfShopOption(newOption);
      previousOptionRef.current = newOption;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.formState.isDirty, form.formState.isSubmitted]);

  // Function to clean form data based on selected option (called during submission)
  const cleanGolfShopData = (formData: CompensationProfileFormInput): CompensationProfileFormInput => {
    const cleanedData = { ...formData };

    if (golfShopOption === "owner") {
      // Clear commission fields when owner is selected
      cleanedData.golfShopCommissionAnnualIncome = null;
      cleanedData.golfShopCommissionPercentage = null;
      cleanedData.ownsGolfShop = true;
    } else if (golfShopOption === "commission") {
      // Clear owner fields when commission is selected
      cleanedData.golfShopOwnershipPercent = null;
      cleanedData.golfShopGrossSales = null;
      cleanedData.golfShopNetIncome = null;
      cleanedData.ownsGolfShop = false;
    } else if (golfShopOption === "none") {
      // Clear all golf shop related fields when none is selected
      cleanedData.golfShopOwnershipPercent = null;
      cleanedData.golfShopGrossSales = null;
      cleanedData.golfShopNetIncome = null;
      cleanedData.golfShopCommissionAnnualIncome = null;
      cleanedData.golfShopCommissionPercentage = null;
      cleanedData.ownsGolfShop = false;
    }

    return cleanedData;
  };

  // Expose the cleaning function to the parent component
  useEffect(() => {
    // @ts-ignore - adding custom property to form
    form.cleanGolfShopData = cleanGolfShopData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [golfShopOption, form]);

  return (
    <div className="space-y-6">
      <div className="text-headline-small">Enter golf shop merchandise concession details.</div>
      <div className="col-span-full">
        <p className="text-title-medium mb-2">Golf Shop Ownership</p>
        <ListItem
          headline="I own the golf shop merchandise concession"
          leading={
            <Radio
              name="golfShopOption"
              label=""
              checked={golfShopOption === "owner"}
              onChange={() => setGolfShopOption("owner")}
            />
          }
        />
        <div className="ps-[40px] flex flex-col gap-4">
          <Controller
            control={form.control}
            name="golfShopOwnershipPercent"
            render={({ field }) => (
              <NumberFormatBase
                value={field.value}
                name="golfShopOwnershipPercent"
                className="w-full"
                customInput={TextField}
                error={!!form.formState.errors.golfShopOwnershipPercent}
                errorText={form.formState.errors.golfShopOwnershipPercent?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                onValueChange={(values) => {
                  field.onChange(values.floatValue ?? null);
                  form.trigger("golfShopOwnershipPercent");
                }}
                leadingIcon="percent"
                label="Percent of Golf Shop Ownership"
                placeholder="Percent of Golf Shop Ownership"
                disabled={golfShopOption !== "owner"}
              />
            )}
          />
          <Controller
            control={form.control}
            name="golfShopGrossSales"
            render={({ field }) => (
              <NumberFormatBase
                value={field.value}
                name="golfShopGrossSales"
                className="w-full"
                customInput={TextField}
                error={!!form.formState.errors.golfShopGrossSales}
                errorText={form.formState.errors.golfShopGrossSales?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                format={currencyFormatterNoDecimals}
                onValueChange={(values) => {
                  field.onChange(values.value ? parseInt(values.value, 10) : null);
                  form.trigger("golfShopGrossSales");
                }}
                leadingIcon="attach_money"
                label="Golf Shop Gross Sales"
                placeholder="Golf Shop Gross Sales"
                disabled={golfShopOption !== "owner"}
              />
            )}
          />
          <Controller
            control={form.control}
            name="golfShopNetIncome"
            render={({ field }) => (
              <NumberFormatBase
                value={field.value}
                name="golfShopNetIncome"
                className="w-full"
                customInput={TextField}
                error={!!form.formState.errors.golfShopNetIncome}
                errorText={form.formState.errors.golfShopNetIncome?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                format={currencyFormatterNoDecimals}
                onValueChange={(values) => {
                  field.onChange(values.value ? parseInt(values.value, 10) : null);
                  form.trigger("golfShopNetIncome");
                }}
                leadingIcon="attach_money"
                label="Total Golf Shop Commission Annual Income"
                placeholder="Total Golf Shop Commission Annual Income"
                disabled={golfShopOption !== "owner"}
              />
            )}
          />
        </div>
        <ListItem
          headline="I do not own the golf shop, but I receive a commission on merchandise sales"
          leading={
            <Radio
              name="golfShopOption"
              label=""
              checked={golfShopOption === "commission"}
              onChange={() => setGolfShopOption("commission")}
            />
          }
        />
        <div className="ps-[40px] flex flex-col gap-4">
          <Controller
            control={form.control}
            name="golfShopCommissionPercentage"
            render={({ field }) => (
              <NumberFormatBase
                value={field.value}
                name="golfShopCommissionPercentage"
                className="w-full"
                customInput={TextField}
                error={!!form.formState.errors.golfShopCommissionPercentage}
                errorText={form.formState.errors.golfShopCommissionPercentage?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                onValueChange={(values) => {
                  field.onChange(values.floatValue ?? null);
                  form.trigger("golfShopCommissionPercentage");
                }}
                leadingIcon="percent"
                label="Percent of Commission"
                placeholder="Percent of Commission"
                disabled={golfShopOption !== "commission"}
              />
            )}
          />
          <Controller
            control={form.control}
            name="golfShopCommissionAnnualIncome"
            render={({ field }) => (
              <NumberFormatBase
                value={field.value}
                name="golfShopCommissionAnnualIncome"
                className="w-full"
                customInput={TextField}
                error={!!form.formState.errors.golfShopCommissionAnnualIncome}
                errorText={form.formState.errors.golfShopCommissionAnnualIncome?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                format={currencyFormatterNoDecimals}
                onValueChange={(values) => {
                  field.onChange(values.value ? parseInt(values.value, 10) : null);
                  form.trigger("golfShopCommissionAnnualIncome");
                }}
                leadingIcon="attach_money"
                label="Annual Commission Income"
                placeholder="Annual Commission Income"
                disabled={golfShopOption !== "commission"}
              />
            )}
          />
        </div>
        <ListItem
          headline="I do not own the golf shop nor receive a commission on merchandise sales"
          leading={
            <Radio
              name="golfShopOption"
              label=""
              checked={golfShopOption === "none"}
              onChange={() => setGolfShopOption("none")}
            />
          }
        />
      </div>
    </div>
  );
}
